import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "./shopInfoModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import RSFNewAutocomplete from "../../../utils/addressAutofill/addressAutocomplete";
import BannerMessage from "../../common/bannerMessage/bannerMessage";

const ShopInfoModal = ({ open, onClose, shopData, setShopData }) => {
    const { language, setUserData, userData } = useContext(CombinedContext);
    const [bannerMessage, setBannerMessage] = useState("");

    const [localData, setLocalData] = useState({
        name: "",
        description: "",
        address: "",
        city: "",
        country: "",
        lat: null,
        lng: null,
    });

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (shopData) {
            setLocalData({
                name: shopData.name || "",
                description: shopData.description || "",
                address: shopData.address || "",
                city: shopData.city || "",
                country: shopData.country || "",
                lat: shopData.lat ?? null,
                lng: shopData.lng ?? null,
            });
        }
    }, [shopData]);

    if (!open) return null;

    const isFormValid = () => {
        return (
            localData.name.trim() &&
            localData.description.trim() &&
            localData.address.trim() &&
            localData.city.trim() &&
            localData.country.trim()
        );
    };

    const handleSave = async () => {
        if (!isFormValid()) {
            setBannerMessage(translations[language].profile.errors.fillAllFields || "Please fill in all fields.");
            return;
        }

        setBannerMessage("");

        const updateData = { ...localData };

        try {
            setSaving(true);
            const res = await companyBasedRequest.updateShopInfo(
                shopData.shopId,
                updateData,
                userData.cognitoTokens.AccessToken
            );

            setUserData((prev) => ({ ...prev, shop: res.shop }));
            onClose();
        } catch (err) {
            console.error("Failed to update shop info:", err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="sf-book-overlay" onClick={onClose}>
            <div className="sf-book-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sf-profile-modal-header">
                    <h2 className="sf-profile-modal-title">{translations[language].profile.modal.shopInfo.title}</h2>
                    <button className="sf-book-close" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="sf-book-body">
                    <h3>{translations[language].profile.modal.shopInfo.subTitle}</h3>
                    <input
                        className="sf-book-input"
                        placeholder={translations[language].profile.modal.shopInfo.name}
                        value={localData.name}
                        onChange={(e) => setLocalData({ ...localData, name: e.target.value })}
                    />

                    <div className="sf-description-wrapper">
                        <textarea
                            className="sf-book-input tall-description"
                            placeholder={translations[language].profile.modal.shopInfo.description}
                            value={localData.description}
                            maxLength={250}
                            onChange={(e) => setLocalData({ ...localData, description: e.target.value })}
                        />

                        <div className="sf-char-count">{localData.description.length}/250</div>
                    </div>

                    <h3>{translations[language].profile.sidebar.location.title}</h3>

                    <RSFNewAutocomplete
                        value={localData.address}
                        onChange={(val) => {
                            setLocalData((prev) => ({
                                ...prev,
                                address: val,
                                lat: null,
                                lng: null,
                            }));
                        }}
                        onSelect={(data) => {
                            setLocalData((prev) => ({
                                ...prev,
                                address: data.address,
                                city: data.city,
                                country: data.country,
                                lat: data.lat,
                                lng: data.lng,
                            }));
                        }}
                        language={language}
                    />

                    <input
                        className="sf-book-input"
                        placeholder={translations[language].profile.modal.shopInfo.city}
                        value={localData.city}
                        onChange={(e) => setLocalData({ ...localData, city: e.target.value })}
                    />

                    <input
                        className="sf-book-input"
                        placeholder={translations[language].profile.modal.shopInfo.country}
                        value={localData.country}
                        onChange={(e) =>
                            setLocalData({
                                ...localData,
                                country: e.target.value,
                            })
                        }
                    />
                </div>

                <div className="sf-base-footer">
                    <button className="btn ghost" onClick={onClose}>
                        {translations[language].calendar.editor.cancel}
                    </button>
                    <button className="btn" onClick={handleSave} disabled={saving}>
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
            <BannerMessage type="error" text={bannerMessage} />
        </div>
    );
};

export default ShopInfoModal;
